import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { logError, TAGS } from '@iwLogger/IwDataDogLogger';
import { useQuery } from '@tanstack/react-query';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';

export const useRCFNextRepayment = (accountId: string) => {
    const stateKey = useStateKey();
    const { data: account } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        select: ({ data }) =>
            data.accounts.find((account) => account.account_id === accountId),
        suspense: true
    });

    if (!account) {
        const errorMessage = `useRCFNextRepayment - RCF Account with id ${accountId} not found`;
        logError(errorMessage, { tags: TAGS.DE_LENDING });
        throw new Error(errorMessage);
    }

    const repaymentSchedule = account.scheduled_payments;

    if (!repaymentSchedule) {
        const errorMessage = `useRCFNextRepayment - RCF Account with id ${accountId} has no repayment schedule`;
        logError(errorMessage, { tags: TAGS.DE_LENDING });
        throw new Error(errorMessage);
    }

    const schedule = repaymentSchedule.schedule;

    if (!schedule || schedule.length === 0) {
        const errorMessage = `useRCFNextRepayment - RCF Account with id ${accountId} has no scheduled payments`;
        logError(errorMessage, { tags: TAGS.DE_LENDING });
        throw new Error(errorMessage);
    }

    const scheduleSummary = repaymentSchedule.summary;

    if (!scheduleSummary) {
        const errorMessage = `useRCFNextRepayment - RCF Account with id ${accountId} has no repayment schedule summary`;
        logError(errorMessage, { tags: TAGS.DE_LENDING });
        throw new Error(errorMessage);
    }

    if (!scheduleSummary.simplified_schedule?.next_repayment) {
        const errorMessage = `useRCFNextRepayment - RCF Account with id ${accountId} has no next repayment on the simplified schedule`;
        logError(errorMessage, { tags: TAGS.DE_LENDING });
        throw new Error(errorMessage);
    }

    const nextRepaymentDate =
        scheduleSummary.simplified_schedule.next_repayment.date;

    const nextRepaymentAmount =
        scheduleSummary.simplified_schedule.next_repayment.amount;

    const outstandingBalance = account.pending_balance!.total!;

    return {
        schedule,
        scheduleSummary,
        outstandingBalance,
        nextRepaymentDate,
        nextRepaymentAmount
    };
};
